import { expected } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { useCertifyModal } from '../../components/CertifyModal/hooks/useCertifyModal.tsx';
import { updateQualityControlStatus } from '../../redux/features/orderSlice.ts';
import {
  selectQualityControlIterations,
  selectQualityControlPassed,
} from '../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { QualityControlReviewContent } from './QualityControlReviewContent.tsx';
import { QUALITY_CONTROL_SUCCESS_DELAY_MS } from './constants.ts';
import {
  QualityControlReviewProgressModal,
  ReviewModalPhase,
} from './internal/QualityControlReviewProgressModal.tsx';
import { useQualityControlSync } from './useQualityControlSync.ts';

export const QualityControlReview = () => {
  const orderID = expected(useParams().orderId);
  const dispatch = useAppDispatch();
  const { qualityControl, isRunningReview, runQualityControl, updateQualityControl } =
    useQualityControlSync(orderID);
  const { certifyModal, openCertifyModal } = useCertifyModal();
  const [reviewModalPhase, setReviewModalPhase] = useState<ReviewModalPhase>('hidden');
  const hasOpenedCertifyModal = useRef(false);
  const hasRunReview = useRef(false);

  const qualityControlPassed = useAppSelector(selectQualityControlPassed);
  const qualityControlIterations = useAppSelector(selectQualityControlIterations);
  const qualityControlIterationsRef = useRef(qualityControlIterations);

  useEffect(() => {
    qualityControlIterationsRef.current = qualityControlIterations;
  }, [qualityControlIterations]);

  useEffect(
    () => () => {
      dispatch(
        updateQualityControlStatus({
          qualityControlPassed: false,
          qualityControlIterations: qualityControlIterationsRef.current,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (isRunningReview) {
      hasRunReview.current = true;
      setReviewModalPhase('running');
      return;
    }

    if (!hasRunReview.current || hasOpenedCertifyModal.current) return;
    setReviewModalPhase(qualityControlPassed ? 'success' : 'hidden');
  }, [isRunningReview, qualityControlPassed]);

  useEffect(() => {
    if (reviewModalPhase !== 'success' || hasOpenedCertifyModal.current) return;

    const timeout = window.setTimeout(() => {
      hasOpenedCertifyModal.current = true;
      setReviewModalPhase('hidden');
      openCertifyModal();
    }, QUALITY_CONTROL_SUCCESS_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [openCertifyModal, reviewModalPhase]);

  return (
    <Flex height="100%" width="100%">
      {certifyModal}
      <QualityControlReviewProgressModal phase={reviewModalPhase} />
      {qualityControl && (
        <QualityControlReviewContent
          qualityControl={qualityControl}
          qualityControlIterations={qualityControlIterations}
          qualityControlPassed={qualityControlPassed}
          isRunningReview={isRunningReview}
          onRunComplianceCheck={() => {
            void runQualityControl();
          }}
          onCertifyAnyway={openCertifyModal}
          onCertify={openCertifyModal}
          updateQualityControl={updateQualityControl}
        />
      )}
    </Flex>
  );
};
